import * as React from 'react';
import { HeartHandshake } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { MatchingRow } from './MatchingRow';
import { MatchingCollapse } from './MatchingCollapse';
import type { Program } from '@/types/program.types';
import type { MatchingDashboardRow, MatchingPerspective } from '../matching-utils';

/** Props for {@link MatchingTable}. */
interface Props {
  perspective: MatchingPerspective;
  tableColSpan: number;
  isLoading: boolean;
  tableData: MatchingDashboardRow[];
  emptyText: string;
  openRowId: string | null;
  programs: Program[];
  onOpenRow: (id: string) => void;
  onCloseRow: () => void;
}

/** Remote matching list (rows + expanded collapse). */
export function MatchingTable({
  perspective,
  tableColSpan,
  isLoading,
  tableData,
  emptyText,
  openRowId,
  programs,
  onOpenRow,
  onCloseRow,
}: Readonly<Props>) {
  const showCapacityColumn = perspective === 'tutor';

  function renderCapacityHeader(): React.ReactNode {
    if (!showCapacityColumn) return null;
    return <th>Capacité</th>;
  }

  /** Tutors show engagement valorisé; students keep partner. */
  function renderLastColumnHeader(): React.ReactNode {
    if (perspective === 'tutor') return <th>Engagement valorisé</th>;
    return <th>Partenaire</th>;
  }

  function renderLoadingRow(): React.ReactNode {
    if (!isLoading) return null;
    return (
      <tr>
        <td colSpan={tableColSpan}>
          <div className="table-empty">
            <Spinner size="lg" label="Chargement..." />
          </div>
        </td>
      </tr>
    );
  }

  function renderEmptyRow(): React.ReactNode {
    if (isLoading || tableData.length > 0) return null;
    return (
      <tr>
        <td colSpan={tableColSpan}>
          <div className="table-empty">
            <HeartHandshake className="table-empty-icon" />
            <p className="table-empty-text">{emptyText}</p>
          </div>
        </td>
      </tr>
    );
  }

  function renderDataRow(item: MatchingDashboardRow, index: number): React.ReactNode {
    const isExpanded = openRowId === item.id;
    function handleRowClick(): void {
      if (isExpanded) {
        onCloseRow();
        return;
      }
      onOpenRow(item.id);
    }
    return (
      <React.Fragment key={item.id}>
        <tr
          className="group animate-fade-in animate-stagger cursor-pointer"
          style={{ animationDelay: `${index * 50}ms` }}
          onClick={handleRowClick}
        >
          <MatchingRow
            data={item}
            perspective={perspective}
            isExpanded={isExpanded}
            programsList={programs}
          />
        </tr>
        {renderExpandedRow(item, isExpanded)}
      </React.Fragment>
    );
  }

  function renderExpandedRow(item: MatchingDashboardRow, isExpanded: boolean): React.ReactNode {
    if (!isExpanded) return null;
    return (
      <tr className="table-row-expanded">
        <td colSpan={tableColSpan} className="p-0">
          <div className="table-row-collapse-content">
            <MatchingCollapse data={item} perspective={perspective} programsList={programs} />
          </div>
        </td>
      </tr>
    );
  }

  function renderDataRows(): React.ReactNode {
    if (isLoading) return null;
    const rows: React.ReactNode[] = [];
    for (let index = 0; index < tableData.length; index++) {
      rows.push(renderDataRow(tableData[index], index));
    }
    return rows;
  }

  return (
    <div className="overflow-x-auto">
      <table className="table-modern">
        <thead>
          <tr>
            <th>Nom complet</th>
            <th>Statut</th>
            <th>Affectabilité</th>
            <th>Programme</th>
            <th>Genre</th>
            <th>Matière</th>
            <th>Niveau</th>
            {renderCapacityHeader()}
            {renderLastColumnHeader()}
          </tr>
        </thead>
        <tbody>
          {renderLoadingRow()}
          {renderEmptyRow()}
          {renderDataRows()}
        </tbody>
      </table>
    </div>
  );
}
